/**
 * Soft!=Live Page212 — Settings Delegations residual liquid (Page123/88/46 + dual).
 * Mount: /settings/delegations (Soft demote → profile deleted).
 * Buses (contract ops — never painted in operator DOM):
 *   get__settings_delegations_status · admin tenant-delegations CRUD
 * Soft residual deleted:
 *  - Soft grant invent on refuse / gated seats
 *  - Soft invent seats while posture loading
 *  - Soft ACL-on theatre when flag off
 *  - Soft SA/super invent of tenant grant desk
 *  - path /operation labels painted in DOM
 */
import { useMemo, useState } from "react";
import { html, _F, Ico, Card, Field, Input, Alert, Ticker, go, KV } from "./ui.js";
import { path, useAction, useApi } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { useSession } from "./session.js";
import { hasExplicitSaSeat, onlyIf } from "./cap-complete.js";

export const TENANT_CAP_OPTS = [
  { id: "tenant_user_admin", label: "User admin" },
  { id: "tenant_share_admin", label: "Sharing" },
  { id: "tenant_audit_read", label: "Audit read" },
  { id: "tenant_full_access", label: "Full access" },
];

/** Path-free operator copy for Delegations desk. Catalogue stays empty until Live says so. */
export function softDelegationsSettingsErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|seat_gated|tenant admin|insufficient|not_authorized|denied/.test(low)) {
    return "Your account cannot manage grants here. Parent tenant admin is required when sharing is on.";
  }
  if (/\b401\b|unauthorized|missing bearer|session/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b422\b|\b400\b|invalid|required|grantee/.test(low)) {
    return "That grant was not accepted. Check the name and capabilities.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/\b503\b|unavailable|acl/.test(low)) {
    return "Grant controls are not ready right now.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/api\/|\/admin\/|\/settings\/delegations|GET |POST |DELETE |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "…";
  return n;
}

/** Soft!=Live Page212: missing contract ops must not crash the desk. */
function safeDelgPath(op, opts) {
  try {
    return path(op, opts);
  } catch (_e) {
    if (op === "get__settings_delegations_status") return "/settings/delegations/status";
    if (op === "get__admin_tenant-delegations") return "/admin/tenant-delegations";
    if (op === "post__admin_tenant-delegations") return "/admin/tenant-delegations";
    if (op === "delete__admin_tenant-delegations_p") {
      const g = opts && opts.pathParams && opts.pathParams[0];
      return g ? "/admin/tenant-delegations/" + encodeURIComponent(g) : "";
    }
    return "";
  }
}

/** Pure: seat chips from Live Settings Delegations posture (Page212) — fail-closed. */
export function delegationsSettingsSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const paint = Number(lim.delegation_paint_max);
  return {
    enabled: p.enabled === true,
    armed: p.armed === true,
    aclArmed: p.tenant_acl_enabled === true,
    surveyAllowed: p.survey_plane_allowed === true,
    writeAllowed: p.write_plane_allowed === true,
    grantAllowed: p.grant_allowed === true,
    revokeAllowed: p.revoke_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    postureLabel: String(p.posture_label || "").trim(),
    paintMax:
      Number.isFinite(paint) && paint > 0 ? Math.floor(paint) : 48,
  };
}

/** Pure: delegation rows. */
export function delegationRowsOf(pack) {
  const root = pack || {};
  const rows = root.items || root.delegations || root.rows || [];
  return Array.isArray(rows) ? rows : [];
}

/** Pure: csv from selected cap ids. */
export function capsCsvOf(selected) {
  const set = selected instanceof Set ? selected : new Set(selected || []);
  return TENANT_CAP_OPTS.map((o) => o.id).filter((id) => set.has(id)).join(", ");
}

/** Pure: KPI strip from Live rows only. */
export function delegationsKpisOf(rows, capsSelected, aclArmed) {
  const list = Array.isArray(rows) ? rows : [];
  const openEnded = list.filter((r) => r && (r.granted_until == null || r.granted_until === "")).length;
  const timed = list.length - openEnded;
  return {
    active: list.length,
    openEnded,
    timed,
    draftCaps: capsSelected instanceof Set ? capsSelected.size : 0,
    aclArmed: !!aclArmed,
  };
}

export function DelegationsDesk({ toast }) {
  const { me } = useSession();
  const _role = String((me && me.role) || "").toLowerCase();

  const posture = useApi(safeDelgPath("get__settings_delegations_status"));
  const post = posture.data || {};
  // Fail-closed while loading — seats fail-closed.
  const seat = delegationsSettingsSeatOf(posture.loading || !posture.data ? {} : post);

  const surveyOn = seat.surveyAllowed;
  const writeOn = seat.writeAllowed;
  const list = useApi(surveyOn ? safeDelgPath("get__admin_tenant-delegations") : null);
  const { run, busy, error, envelope } = useAction();
  const rows = useMemo(() => {
    const all = delegationRowsOf(list.data);
    return all.slice(0, seat.paintMax);
  }, [list.data, seat.paintMax]);
  const [grantee, setGrantee] = useState("");
  const [caps, setCaps] = useState(() => new Set());
  const [until, setUntil] = useState("");
  const kpis = useMemo(
    () => delegationsKpisOf(rows, caps, seat.aclArmed),
    [rows, caps, seat.aclArmed],
  );

  const toggleCap = (id) => {
    setCaps((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const reRead = () => {
    if (!seat.reReadAllowed) {
      toast && toast("Refresh is not available on this seat.", "warn");
      return;
    }
    posture.reload && posture.reload();
    list.reload && list.reload();
    toast && toast("Delegations refreshed", "go");
  };

  const save = async () => {
    if (!seat.grantAllowed) {
      toast && toast("Saving grants is not available on this seat.", "warn");
      return;
    }
    const g = grantee.trim();
    const csv = capsCsvOf(caps);
    if (!g || !csv) {
      toast && toast("Enter a person and pick at least one capability.", "warn");
      return;
    }
    let granted_until;
    if (until.trim()) {
      const n = Number(until);
      if (!Number.isFinite(n) || n < 1) {
        toast && toast("Expiry must be unix seconds, or leave empty.", "warn");
        return;
      }
      granted_until = Math.floor(n);
    }
    const out = await run(safeDelgPath("post__admin_tenant-delegations"), {
      body: { grantee_username: g, capabilities_csv: csv, granted_until },
    });
    if (!out) {
      toast && toast(softDelegationsSettingsErrorOf(error) || "Could not save the grant.", "bad");
      return;
    }
    toast && toast("Grant saved for " + g, "go");
    setGrantee("");
    setCaps(new Set());
    setUntil("");
    list.reload && list.reload();
  };

  const revoke = async (g) => {
    if (!seat.revokeAllowed) {
      toast && toast("Revoking grants is not available on this seat.", "warn");
      return;
    }
    if (!g) return;
    if (!window.confirm("Revoke all capabilities for " + g + "?")) return;
    const out = await run(safeDelgPath("delete__admin_tenant-delegations_p", { pathParams: [g] }), {
      method: "DELETE",
    });
    if (!out) {
      toast && toast(softDelegationsSettingsErrorOf(error) || "Could not revoke. Nothing was changed.", "bad");
      return;
    }
    toast && toast("Revoked " + g, "warn");
    list.reload && list.reload();
  };

  const denied = !!(list.error && /\b40[13]\b|tenant admin|forbidden|insufficient/i.test(list.error || ""));
  const offlineAcl = !seat.aclArmed && seat.detail === "tenant_acl_disabled";

  if (!surveyOn && !writeOn) {
    return html`<div className="delg-desk delg-desk--liquid"
      data-cmc-page="page123-delegations" data-cmc-delg="page46" data-cmc-legacy="page88-delegations"
      data-cmc-residual="page212-delegations">
      <div className="delg-desk__rail">
        <div>
          <h2 className="delg-desk__title">Grants sealed</h2>
        </div>
        <div className="btn-row delg-desk__acts">
          <button type="button" className="btn btn--ghost btn--sm"
            disabled=${!seat.reReadAllowed}
            onClick=${reRead}>
            <${Ico} d="refresh" w=${13}/> Refresh</button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/settings/audit")}>Audit export</button>
        </div>
      </div>
      <${Alert} tone="info" title="Delegations sealed"
        say=${offlineAcl
          ? "Tenant sharing is off on this deployment. Grant tables stay empty until Live opens them."
          : "Your seat cannot list or write grants. Parent tenant admin when sharing is on."}/>
      <div className="delg-desk__kpis" role="group" aria-label="Delegation posture">
        <div className="delg-desk__kpi" data-on="0"><span>Grant list</span><b>sealed</b><em>read</em></div>
        <div className="delg-desk__kpi" data-on="0"><span>Write</span><b>sealed</b><em>grant/revoke</em></div>
        <div className="delg-desk__kpi" data-on=${seat.aclArmed ? "1" : "0"}><span>Sharing</span><b>${seat.aclArmed ? "on" : "off"}</b><em>deployment</em></div>
        <div className="delg-desk__kpi" data-on="0"><span>Honesty</span><b>Live</b><em>no invent</em></div>
      </div>
    </div>`;
  }

  return html`<div className="delg-desk delg-desk--liquid"
    data-cmc-page="page123-delegations" data-cmc-delg="page46" data-cmc-legacy="page88-delegations"
    data-cmc-residual="page212-delegations">
    <div className="delg-desk__rail">
      <div>
        <h2 className="delg-desk__title">Organization grants</h2>
      </div>
      <div className="btn-row delg-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" disabled=${busy || !seat.reReadAllowed}
          onClick=${reRead}>
          <${Ico} d="refresh" w=${13}/> Refresh</button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/settings/audit")}>Audit export</button>
        ${onlyIf(hasExplicitSaSeat(me), html`<button type="button" className="btn btn--go btn--sm"
          onClick=${() => go("/sa/team")}>SA Staff</button>`)}
      </div>
    </div>

    ${offlineAcl || denied ? html`<${Alert} tone=${offlineAcl ? "warn" : "info"}
      title=${offlineAcl ? "Tenant sharing off" : "Grant list gated on this seat"}
      say=${offlineAcl
        ? "Sharing is off. Grant tables stay empty until tenant sharing is armed on deployment."
        : softDelegationsSettingsErrorOf(list.error)}/>` : null}
    ${envelope ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>` : null}

    <div className="delg-desk__kpis" role="group" aria-label="Delegation KPIs">
      ${[
        ["Active", surveyOn ? kpis.active : "sealed", surveyOn && kpis.active ? "1" : "0", "list"],
        ["Timed", surveyOn ? kpis.timed : "—", surveyOn && kpis.timed ? "1" : "0", "with expiry"],
        ["Open-ended", surveyOn ? kpis.openEnded : "—", surveyOn && kpis.openEnded ? "1" : "0", "no expiry"],
        ["Draft caps", writeOn ? kpis.draftCaps : "sealed", writeOn && kpis.draftCaps ? "1" : "0", seat.aclArmed ? "sharing on" : "sharing off"],
      ].map(([k, v, on, em]) => html`
        <div key=${k} className="delg-desk__kpi" data-on=${on}>
          <span>${k}</span>
          <b>${typeof v === "number" ? html`<${Ticker} value=${v}/>` : v}</b>
          <em>${em}</em>
        </div>`)}
    </div>

    <div className="delg-desk__grid">
      ${!surveyOn ? html`
        <${Alert} tone="info" title="Grant list sealed"
          say="Cannot invent grant rows without a list seat."/>`
      : html`
      <${Card} title="Active grants" meta="server list">
        ${list.loading && !list.data
          ? html`<p className="note">Loading grants…</p>`
          : list.error
            ? html`<${Alert} tone=${denied ? "info" : "warn"}
                title="Grants unavailable"
                say=${softDelegationsSettingsErrorOf(list.error)}/>`
            : !rows.length
              ? html`<p className="note">No grants yet — empty until Live has rows.</p>`
              : html`<div className="rows">
                  ${rows.map((r, i) => {
                    const g = String(r.grantee_username || "");
                    return html`
                      <div key=${g || i} className="row">
                        <span className="main">
                          <b>${g || "—"}</b>
                          <span>${String(r.capabilities_csv || "—")}
                            · until ${r.granted_until != null ? String(r.granted_until) : "open"}</span>
                        </span>
                        ${onlyIf(seat.revokeAllowed, html`<button type="button" className="btn btn--danger btn--xs"
                          disabled=${busy || !g}
                          onClick=${() => revoke(g)}>Revoke</button>`)}
                      </div>`;
                  })}
                </div>`}
      <//>`}

      ${!writeOn ? html`
        <${Alert} tone="info" title="Write sealed"
          say="Cannot invent Save or Revoke without a write seat."/>`
      : html`
      <${Card} title="Grant or replace" meta="server write">
        <${Field} label="Person username">
          <${Input} icon="user" value=${grantee}
            onChange=${(e) => setGrantee(e.target.value)}
            placeholder="staff.ops"/>
        <//>
        <p className="note" style=${{ marginBottom: 8 }}>Capabilities (multi-select)</p>
        <div className="delg-desk__caps">
          ${TENANT_CAP_OPTS.map((o) => html`
            <button key=${o.id} type="button" className="delg-desk__cap"
              data-on=${caps.has(o.id) ? "1" : "0"}
              disabled=${!seat.grantAllowed}
              onClick=${() => toggleCap(o.id)}>
              <span className="sw"><i/></span>${o.label}
            </button>`)}
        </div>
        <${Field} label="Expires (unix seconds, optional)">
          <${Input} icon="clock" value=${until}
            onChange=${(e) => setUntil(e.target.value)}
            placeholder="Leave empty for open-ended"/>
        <//>
        <button type="button" className="btn btn--go btn--sm"
          disabled=${busy || !seat.grantAllowed || !grantee.trim() || !capsCsvOf(caps)}
          onClick=${save} style=${{ marginTop: 10 }}>
          <${Ico} d="check" w=${13}/> ${busy ? "Saving…" : "Save grant"}</button>
        ${error ? html`<${Alert} tone="bad" title="Write refused"
          say=${softDelegationsSettingsErrorOf(error)}/>` : null}
      <//>`}
    </div>
  </div>`;
}

export default DelegationsDesk;
